'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import {
  PageGeneralRulesV2Schema,
  GeneralRulesV2Item,
  GeneralRulesV2Group,
} from '@/sanity/pages/PageGeneralRulesV2Template';
import { PillTabs } from '@/components/pageComponents/global/PillTabs';
import RuleCard from './RuleCard';

const ease = [0.25, 0.1, 0.25, 1] as const;

type SubTab = {
  key: string;
  label: string;
  variant: 'items' | 'groups';
  items?: GeneralRulesV2Item[];
  groups?: GeneralRulesV2Group[];
};

function GroupSection({ group }: { group: GeneralRulesV2Group }) {
  const items = group.items ?? [];
  return (
    <div className="w-full flex flex-col gap-[2.5rem]">
      {group.title && (
        <div className="w-full border-t border-black/10 pt-[1.25rem]">
          <p className="mb-0 font-geist font-medium text-[3rem] leading-[1.33] tracking-[-0.09rem] text-black">
            {group.title}
          </p>
        </div>
      )}
      <div className="flex flex-col gap-[2.5rem]">
        {items.map((it, i) => (
          <RuleCard key={it._key ?? i} item={it} />
        ))}
      </div>
    </div>
  );
}

export default function RulesSection({
  data,
}: {
  data: PageGeneralRulesV2Schema;
}) {
  const [mainTab, setMainTab] = useState<'crypto' | 'futures'>('crypto');
  const [subTab, setSubTab] = useState<string>('tab1');

  useEffect(() => {
    setSubTab('tab1');
  }, [mainTab]);

  const cryptoSubTabs: SubTab[] = [
    {
      key: 'tab1',
      label: data.cryptoTab1Label ?? 'Core Rules',
      variant: 'items',
      items: data.cryptoTab1Items ?? [],
    },
    {
      key: 'tab2',
      label: data.cryptoTab2Label ?? 'Trading Conditions',
      variant: 'items',
      items: data.cryptoTab2Items ?? [],
    },
    {
      key: 'tab3',
      label: data.cryptoTab3Label ?? 'Payout & Profit Split',
      variant: 'items',
      items: data.cryptoTab3Items ?? [],
    },
    {
      key: 'tab4',
      label: data.cryptoTab4Label ?? 'Breach Logic & Consequences',
      variant: 'items',
      items: data.cryptoTab4Items ?? [],
    },
    {
      key: 'tab5',
      label: data.cryptoTab5Label ?? 'Prohibited Practices',
      variant: 'groups',
      groups: data.cryptoTab5Groups ?? [],
    },
  ];

  const futuresSubTabs: SubTab[] = [
    {
      key: 'tab1',
      label: data.futuresTab1Label ?? 'Core Rules',
      variant: 'items',
      items: data.futuresTab1Items ?? [],
    },
    {
      key: 'tab2',
      label: data.futuresTab2Label ?? 'Trading Conditions',
      variant: 'items',
      items: data.futuresTab2Items ?? [],
    },
    {
      key: 'tab3',
      label: data.futuresTab3Label ?? 'Payout & Profit Split',
      variant: 'items',
      items: data.futuresTab3Items ?? [],
    },
    {
      key: 'tab4',
      label: data.futuresTab4Label ?? 'Breach Logic & Consequences',
      variant: 'items',
      items: data.futuresTab4Items ?? [],
    },
    {
      key: 'tab5',
      label: data.futuresTab5Label ?? 'Prohibited Practices',
      variant: 'groups',
      groups: data.futuresTab5Groups ?? [],
    },
  ];

  const activeSubTabs = mainTab === 'crypto' ? cryptoSubTabs : futuresSubTabs;
  const currentTab =
    activeSubTabs.find((t) => t.key === subTab) ?? activeSubTabs[0];

  const mainTabOptions = [
    { value: 'crypto', label: data.cryptoTabLabel ?? 'Crypto & CFDs' },
    { value: 'futures', label: data.futuresTabLabel ?? 'Futures' },
  ];

  return (
    <section className="relative w-full pb-[15rem] lg:pb-[20rem]">
      <div className="mx-auto flex w-[min(146rem,calc(100%-4rem))] lg:w-[min(146rem,calc(100%-8rem))] flex-col items-center gap-[4rem]">
        {/* ── Main tabs: Crypto & CFDs | Futures ─────────────────────────── */}
        <PillTabs
          options={mainTabOptions}
          value={mainTab}
          onChange={(v) => setMainTab(v as 'crypto' | 'futures')}
          layoutId="general-rules-v2-main-tab"
        />

        {/* ── Sub-tabs ───────────────────────────────────────────────────── */}
        <div className="w-full flex flex-nowrap justify-start lg:flex-wrap lg:justify-center gap-[1.25rem] overflow-x-auto lg:overflow-visible [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {activeSubTabs.map((tab) => {
            const isActive = subTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setSubTab(tab.key)}
                className={cn(
                  'shrink-0 rounded-[6.875rem] border px-[2.5rem] h-[5.75rem] font-geist leading-none tracking-[-0.045rem] whitespace-nowrap transition-colors',
                  isActive
                    ? 'bg-black text-white border-black font-normal text-[2.25rem]'
                    : 'bg-[#f2f2f2] text-black/70 border-black/10 hover:border-black/30 font-normal text-[2.25rem]',
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── Content ────────────────────────────────────────────────────── */}
        <div className="w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${mainTab}-${subTab}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.3, ease }}
              className={cn(
                'flex flex-col',
                currentTab?.variant === 'groups'
                  ? 'gap-[6rem] lg:gap-[8rem]'
                  : 'gap-[2.5rem]',
              )}
            >
              {currentTab?.variant === 'items' &&
                (currentTab.items ?? []).map((item, idx) => (
                  <motion.div
                    key={item._key ?? idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, ease, delay: idx * 0.05 }}
                  >
                    <RuleCard item={item} />
                  </motion.div>
                ))}

              {currentTab?.variant === 'groups' &&
                (currentTab.groups ?? []).map((group, idx) => (
                  <motion.div
                    key={group._key ?? idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, ease, delay: idx * 0.05 }}
                  >
                    <GroupSection group={group} />
                  </motion.div>
                ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
